import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { BarChart3, RefreshCw, Shield } from 'lucide-react';
import type { HaloDiagnostics } from './types';
import { formatDateShort, safeLocaleNumber, daysAgo, today, timeAgo } from './types';
import { StatusBadge } from './StatusBadge';

function computeHealthScore(baseline: number, attribution: number): number {
  const baselineScore = Math.min(baseline / 90, 1) * 50;
  const attrScore = Math.min(attribution / 90, 1) * 50;
  return Math.round(baselineScore + attrScore);
}

function getHealthColor(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-500';
}

function getHealthRingColor(score: number): string {
  if (score >= 80) return 'stroke-emerald-500';
  if (score >= 50) return 'stroke-amber-500';
  return 'stroke-red-500';
}

export function DiagnosticsSection({ storeId }: { readonly storeId: number }) {
  const startDate = daysAgo(90);
  const endDate = today();

  const { data: diagnostics, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'halo-diagnostics', storeId, startDate, endDate],
    queryFn: async ({ signal }) => {
      const res = await api.get<HaloDiagnostics>(
        `/api/v1/ingestion/admin/halo-diagnostics?storeId=${storeId}&startDate=${startDate}&endDate=${endDate}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load diagnostics');
      return res.data;
    },
  });

  const baselineRows = diagnostics?.baseline?.rowCount ?? 0;
  const attrRows = diagnostics?.attribution?.rowCount ?? 0;
  const lastComputed = diagnostics?.attribution?.maxDate ?? diagnostics?.baseline?.maxDate ?? null;
  const coverageStart = diagnostics?.baseline?.minDate ?? null;
  const coverageEnd = diagnostics?.baseline?.maxDate ?? null;

  const healthScore = useMemo(() => computeHealthScore(baselineRows, attrRows), [baselineRows, attrRows]);

  function getBaselineStatus(): 'good' | 'partial' | 'missing' {
    if (baselineRows > 30) return 'good';
    if (baselineRows > 0) return 'partial';
    return 'missing';
  }

  function getAttributionStatus(): 'good' | 'partial' | 'missing' {
    if (attrRows > 30) return 'good';
    if (attrRows > 0) return 'partial';
    return 'missing';
  }

  const circumference = 2 * Math.PI * 18;
  const dashOffset = circumference - (healthScore / 100) * circumference;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
            <BarChart3 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Diagnostics</h3>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Refresh diagnostics"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      ) : diagnostics ? (
        <div>
          {/* Health Score Ring */}
          <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-950 rounded-lg">
            <div className="relative w-12 h-12 shrink-0">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="18" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                <circle
                  cx="20" cy="20" r="18" fill="none"
                  className={getHealthRingColor(healthScore)}
                  strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-xs font-bold ${getHealthColor(healthScore)}`}>{healthScore}</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Health Score</span>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                Based on baseline and attribution row coverage
              </p>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Baseline Rows</span>
                <StatusBadge status={getBaselineStatus()} />
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {safeLocaleNumber(baselineRows)}
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Attribution Rows</span>
                <StatusBadge status={getAttributionStatus()} />
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {safeLocaleNumber(attrRows)}
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">Last Computed</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {lastComputed ? formatDateShort(lastComputed) : 'Never'}
              </span>
              {lastComputed && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 block mt-0.5">{timeAgo(lastComputed)}</span>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-gray-950 rounded-lg p-3">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">Date Range</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {coverageStart && coverageEnd
                  ? `${formatDateShort(coverageStart)} - ${formatDateShort(coverageEnd)}`
                  : 'No coverage'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No diagnostics available</p>
      )}
    </div>
  );
}
